import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faExclamationTriangle,
  faFile,
  faFileExcel,
  faFileImage,
  faFilePdf,
  faFilePowerpoint,
  faFileText,
  faFileWord,
  faXmark,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { cn } from "../../lib/utils";
import { Dots } from "../Indicator/Dots";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

type ChipFile = {
  name: string;
  mimeType?: string;
};

const EXTENSION_TO_MIME_TYPE = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain",
  md: "text/plain",
  gif: "image/gif",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
};

export const getFileIconByName = (name: string) => {
  const extension = name
    .split(".")
    .pop() as keyof typeof EXTENSION_TO_MIME_TYPE;

  return getFileIconByMimeType(
    extension in EXTENSION_TO_MIME_TYPE
      ? EXTENSION_TO_MIME_TYPE[extension]
      : "",
  );
};

const getFileIconByMimeType = (
  mimeType: (typeof EXTENSION_TO_MIME_TYPE)[keyof typeof EXTENSION_TO_MIME_TYPE],
) => {
  switch (mimeType) {
    case "application/pdf":
      return faFilePdf;
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return faFileWord;
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return faFileExcel;
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      return faFilePowerpoint;
    case "text/plain":
      return faFileText;
    case "image/gif":
      return faFileImage;
    case "image/jpeg":
      return faFileImage;
    case "image/png":
      return faFileImage;
    default:
      return faFile;
  }
};

export const FileChip = <T extends ChipFile | File>({
  file,
  onRemove,
  loading,
  verified,
  unavailable,
}: {
  file: T;
  onRemove?: (file: T) => void;
  loading?: boolean;
  verified?: boolean;
  unavailable?: boolean | string;
}) => {
  return (
    <Tooltip>
      {unavailable && (
        <TooltipContent>
          {typeof unavailable === "string" ? unavailable : "Unavailable"}
        </TooltipContent>
      )}
      <TooltipTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onRemove?.(file)}
          className={cn("flex items-center gap-2", {
            "line-through": unavailable,
          })}
        >
          <FontAwesomeIcon
            icon={
              "mimeType" in file && file.mimeType
                ? getFileIconByMimeType(file.mimeType)
                : getFileIconByName(file.name)
            }
          />
          <div className="truncate text-xs">{file.name}</div>
          {loading ? <Dots /> : null}
          {verified ? <FontAwesomeIcon icon={faCheck} /> : null}
          {unavailable ? (
            <FontAwesomeIcon icon={faExclamationTriangle} />
          ) : null}
          {onRemove && <FontAwesomeIcon icon={faXmark} />}
        </Button>
      </TooltipTrigger>
    </Tooltip>
  );
};
